// Helper puri per createMailStore: raggruppamento thread, ordinamento,
// conteggio non letti, propagazione stato letto/non letto.
// Logica identica a quella originale di gmailStore.ts.

import type { MailMessage, MailThread } from './types';
import type { MailAdapter, MailModifyAction } from './mailAdapter';

// Raggruppa i messaggi per thread e costruisce i MailThread ordinati:
// non letti in cima, poi per data del messaggio piu` recente (discendente).
export function buildThreadsFromMessages(messages: MailMessage[]): MailThread[] {
  const threadMap = new Map<string, MailMessage[]>();
  for (const msg of messages) {
    if (!threadMap.has(msg.thread_id)) {
      threadMap.set(msg.thread_id, []);
    }
    threadMap.get(msg.thread_id)!.push(msg);
  }

  const threads: MailThread[] = [];
  for (const [threadId, msgs] of threadMap) {
    msgs.sort((a, b) => new Date(b.internal_date).getTime() - new Date(a.internal_date).getTime());
    const latest = msgs[0];
    threads.push({
      thread_id: threadId,
      messages: msgs,
      latest_message: latest,
      subject: latest.subject || '(Nessun oggetto)',
      participant_count: new Set(msgs.map(m => m.from_email)).size,
      has_unread: msgs.some(m => !m.is_read),
      is_starred: msgs.some(m => m.is_starred),
    });
  }

  threads.sort((a, b) => {
    if (a.has_unread !== b.has_unread) return a.has_unread ? -1 : 1;
    return new Date(b.latest_message.internal_date).getTime() - new Date(a.latest_message.internal_date).getTime();
  });

  return threads;
}

// Il contatore "da leggere" conta le CONVERSAZIONI in INBOX con almeno un messaggio
// non letto, come Gmail. Prima contava i singoli messaggi: aprire una conversazione
// con 3 risposte non lette faceva scendere il numero di 3 ("ne trovo meno da leggere").
const isUnreadInbox = (m: MailMessage) => !m.is_read && (m.label_ids || []).includes('INBOX');

// Conversazioni non lette in INBOX a partire dalla lista piatta.
export function countUnreadFromMessages(messages: MailMessage[]): number {
  return new Set(messages.filter(isUnreadInbox).map(m => m.thread_id)).size;
}

// Conversazioni non lette in INBOX a partire dai thread.
export function countUnreadFromThreads(threads: MailThread[]): number {
  return threads.filter(t => t.messages.some(isUnreadInbox)).length;
}

// Il contatore e` quello della Posta in arrivo: si ricalcola solo quando la lista
// caricata e` l'INBOX. Nelle altre viste (Inviati, Preferiti, etichette) i thread
// non rappresentano l'INBOX e il badge crollava a valori falsi: si tiene il precedente.
export function unreadCountForView(activeLabel: string, threads: MailThread[], previous: number): number {
  return activeLabel === 'INBOX' ? countUnreadFromThreads(threads) : previous;
}

// Divide la lista in "Da leggere" / "Tutto il resto". Le conversazioni aperte in
// questa vista (`keptThreadIds`) restano nel gruppo "Da leggere", al loro posto
// e senza grassetto, finche` la vista non si ricarica (cambio cartella, ricerca,
// sincronizza a mano, riapertura della pagina): e` il comportamento di Gmail.
// Prima la conversazione appena letta saltava subito in fondo a "Tutto il resto".
// `unreadCount` resta il numero vero di conversazioni ancora da leggere.
export function partitionThreadsForList(
  threads: MailThread[],
  keptThreadIds: readonly string[],
): { unread: MailThread[]; read: MailThread[]; unreadCount: number } {
  const kept = new Set(keptThreadIds);
  const time = (t: MailThread) => new Date(t.latest_message.internal_date).getTime();
  const unread = threads
    .filter(t => t.has_unread || kept.has(t.thread_id))
    .sort((a, b) => time(b) - time(a));
  const read = threads.filter(t => !t.has_unread && !kept.has(t.thread_id));
  return { unread, read, unreadCount: threads.filter(t => t.has_unread).length };
}

// Propaga lo stato letto/non letto al provider (rimuove/aggiunge label UNREAD).
// Mappa gli id riga DB → message_id usando lo stato corrente. Fire-and-forget.
export function propagateReadState(
  getMessages: () => MailMessage[],
  adapter: MailAdapter,
  org: string,
  user: string,
  messageIds: string[],
  action: Extract<MailModifyAction, 'markRead' | 'markUnread'>,
) {
  const all = getMessages();
  const providerIds = Array.from(new Set(
    all.filter(m => messageIds.includes(m.id)).map(m => m.message_id).filter(Boolean)
  ));
  if (providerIds.length === 0) return Promise.resolve();
  return Promise.all(providerIds.map(pid => adapter.modify(org, user, pid, action))).then(() => undefined, () => undefined);
}
